import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import { DatePicker, Dropdown, IDropdownOption, TextField, Checkbox } from 'office-ui-fabric-react';

import { ApiUrls, PartTimeTzv, Permissions } from '@app/AppConstants';
import { ContractTypeModel, ContractType, PersonFunctionViewModel, ContractPartTimeSplitType, PersonContractViewModel } from '@app/Models';

import { ValidationHelper } from '@app/Services/Validation';
import ApiService from '@app/Services/ApiService';
import DateTimeService from '@app/Services/DateTimeService';
import { DateTimePickerService } from '@app/Services/DateTimePickerService';
import { securityService } from '@app/Services/SecurityService';
import { modalService, IModalDialogContent, ModalButtonType, ModalWindow, ModalDialogOptions } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

import { NotificationHandler } from '@app/Components/ToastNotification';
import { ContractTypeModelDropdown } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractTypeModelDropdown';
import { ContractPartTimeMonthBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeMonthBlock';
import { ContractPartTimeDayBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeDayBlock';
import { ContractPartTimeVariableBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeVariableBlock';
import { ContractPartTimeVariableDayBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeVariableDayBlock';
import { ContractPartTimeFixBlockModelBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeFixBlockModelBlock';
import { ContractPartTimeFixDayBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeFixDayBlock';
import { ContractPartTimeSpecialBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeSpecialBlock';
import { isCabinAt } from '@app/Pages/PersonInfo/Components/Helpers/personFunctionHelper';

export type NewContractDialogProps = {
    personId: string;
    personFunctions: PersonFunctionViewModel[];
    selectedContract: PersonContractViewModel;
};

@observer
export class NewContractModelDialog extends React.Component<NewContractDialogProps, {}> implements IModalDialogContent<void> {
    private _store: NewContractStore;

    constructor(props: NewContractDialogProps) {
        super(props);
        this._store = new NewContractStore(props);
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { hasPermission } = securityService;
        return {
            title: 'New Contract Model',
            width: '450px',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }, {
                type: ModalButtonType.Save,
                isDisabled: !this._store.isValidForm || !hasPermission(Permissions.EditObjectPersonContractTZVPart, !AzureAuth.isReadOnly),
                onClick: async () => {
                    if (await this._store.onSave()) {
                        window.close(ModalButtonType.Save);
                    }
                }
            }],
        };
    }

    render() {
        const minDate = DateTimeService.addDays(this._store.selectedContract.dateFrom, 1);
        return (
            <div className="person-info-dialog">
                <DatePicker
                    label="Date From"
                    className="date-field"
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectFromDate}
                    isRequired={true}
                    value={this._store.dateFrom || undefined}
                    minDate={minDate}
                    maxDate={this._store.selectedContract.dateTo}
                />
                <Dropdown
                    label="Contract"
                    options={this._store.contractTypeOptions}
                    onChange={this._store.onChangeType}
                    required={true}
                    selectedKey={this._store.type as string}
                    disabled
                />
                <ContractTypeModelDropdown
                    type={this._store.type}
                    isCabin={this._store.isCabin}
                    typeModel={this._store.typeModel}
                    onChange={this._store.onChangeTypeModel}
                    disabled={!this._store.canChangeTypeModelPart || !this._store.dateFrom}
                />
                {this._store.typeModel === ContractTypeModel.TZV_Month &&
                    <ContractPartTimeMonthBlock
                        value={this._store.partTimeMonthContractDefinition}
                        onChange={this._store.onChangePartTimeMonthContractDefinition}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_Day &&
                    <ContractPartTimeDayBlock
                        onChangeContractDefinition={this._store.onChangePartTimeDayContractDefinition}
                        onChangeSplitType={this._store.onChangePartTimeDaySplitType}
                        contractDefinition={this._store.partTimeDayContractDefinition}
                        splitType={this._store.partTimeDaySplitType}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_VariableBlock &&
                    <ContractPartTimeVariableBlock
                        onChangeBlockLength={this._store.onChangePartTimeVariableBlockLength}
                        onChangeBlockPerYear={this._store.onChangePartTimeVariableBlockPerYear}
                        blockLength={this._store.partTimeVariableBlockLength}
                        blockPerYear={this._store.partTimeVariableBlockPerYear}
                    />
                }
                {(this._store.typeModel === ContractTypeModel.TZV_VariableDay || this._store.typeModel === ContractTypeModel.TZV_VariableDay_LhSff) &&
                    <ContractPartTimeVariableDayBlock
                        onChangeBlockLength={this._store.onChangePartTimeVariableDayBlockLength}
                        onChangeSplitType={this._store.onChangePartTimeVariableDaySplitType}
                        blockLength={this._store.partTimeVariableDayBlockLength}
                        blockSplitType={this._store.partTimeVariableDaySplitType}
                        typeModel={this._store.typeModel}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_FixBlock &&
                    <>
                        <ContractPartTimeFixBlockModelBlock
                            onChangeBlockDays={this._store.onChangePartTimeFixBlockDays}
                            onChangeBlockFrequency={this._store.onChangePartTimeFixBlockFrequency}
                            onChangeBlockBeginDay={this._store.onChangePartTimeFixBlockBeginDay}
                            onChangeBlockBeginMonth={this._store.onChangePartTimeFixBlockBeginMonth}
                            blockDays={this._store.partTimeFixBlockDays}
                            blockFrequency={this._store.partTimeFixBlockFrequency}
                            blockBeginDay={this._store.partTimeFixBlockBeginDay}
                            blockBeginMonth={this._store.partTimeFixBlockBeginMonth}
                        />
                        {this._store.partTimeFixBlockDays && this._store.partTimeFixBlockFrequency && !this._store.isValidOffLengthFixBlock &&
                            <span className="ms-TextField-errorMessage errorMessage">{this._store.offLengthFixBlockErrorMessage}</span>
                        }
                    </>
                }
                {(this._store.typeModel === ContractTypeModel.TZV_FixDay || this._store.typeModel === ContractTypeModel.TZV_FixDay_Sh || this._store.typeModel === ContractTypeModel.TZV_FixDay_Mff) &&
                    <ContractPartTimeFixDayBlock
                        onChangeContractDefinition={this._store.onChangePartTimeFixDayContractDefinition}
                        onChangeSplitType={this._store.onChangePartTimeFixDaySplitType}
                        onChangeStartWeekPerContract={this._store.onChangePartTimeFixDaysStartWeekPerContract}
                        onChangeDaysPerWeek={this._store.onChangePartTimeFixDaysPerWeek}
                        onChangeFirstDay={this._store.onChangePartTimeFixDayFirstDay}
                        onChangeSecondDay={this._store.onChangePartTimeFixDaySecondDay}
                        onChangeReason={this._store.onChangePartTimeFixDayReason}
                        daysPerWeek={this._store.partTimeFixDaysPerWeek}
                        firstDay={this._store.partTimeFixDayFirstDay}
                        secondDay={this._store.partTimeFixDaySecondDay}
                        splitType={this._store.partTimeFixDaySplitType}
                        reason={this._store.partTimeFixDayReason}
                        isCabin={this._store.isCabin}
                        typeModel={this._store.typeModel}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_Spez &&
                    <ContractPartTimeSpecialBlock
                        onChangeDaysCount={this._store.onChangePartTimeSpecialDaysCount}
                        onChangeVacEntitlement={this._store.onChangePartTimeSpecialVacEntitlement}
                        daysCount={this._store.partTimeSpecialDaysPerYear}
                        vacEntitlement={this._store.partTimeSpecialVacEntitlement}
                    />
                }
                <Checkbox
                    label="External Employee"
                    checked={this._store.isExternalEmployee ?? false}
                    onChange={this._store.onCheckboxChange}
                    className="person-info dialog-inline-fields"
                    disabled
                />
                <TextField
                    label="Employment"
                    onKeyPress={ValidationHelper.validateFloat}
                    maxLength={5}
                    suffix="%"
                    validateOnFocusOut
                    onGetErrorMessage={this.getErrorMessageForEmployment}
                    value={this._store.partTimePercentage !== null ? this._store.partTimePercentage.toString() : ''}
                    onChange={this._store.onPartTimeChange}
                    disabled
                />
                {this._store.needToShowEmploymentWarning &&
                    <p className="ms-TextField-errorMessage warningMessage">For TZV contract employment should be less than 100 %</p>
                }
                <TextField
                    label="Office"
                    onKeyPress={ValidationHelper.validateFloat}
                    maxLength={5}
                    suffix="%"
                    validateOnFocusOut
                    onGetErrorMessage={this.getErrorMessageForOffice}
                    value={this._store.officePercentage !== null ? this._store.officePercentage.toString() : ''}
                    onChange={this._store.onOfficeChange}
                    disabled
                />
                <TextField
                    label="Flight Duty"
                    maxLength={5}
                    suffix="%"
                    value={this._store.flyingDutyValue ? parseFloat(this._store.flyingDutyValue).toString() : ''}
                    disabled
                />
                {securityService.hasPermission(Permissions.ViewEditContractComment, !AzureAuth.isReadOnly) ?
                    <TextField
                        label="Comment"
                        value={this._store.comment || ''}
                        multiline
                        rows={1}
                        onChange={this._store.onChangeComment}
                    /> : null}
                {this._store.validationMessage && <p className="ms-TextField-errorMessage errorMessage">{this._store.validationMessage}</p>}
            </div>
        );
    }

    private getErrorMessageForEmployment = (value: string): string => {
        if (this._store.officePercentage && this._store.officePercentage > parseFloat(value)) {
            return 'Office can\'t be greater than employment';
        }
        if (value && (parseFloat(value) > 100)) {
            return 'Max value is 100%';
        }
        return '';
    };

    private getErrorMessageForOffice = (value: string): string => {
        if (this._store.partTimePercentage && this._store.partTimePercentage < parseFloat(value)) {
            return 'Office can\'t be greater than employment';
        }
        if (value && (parseFloat(value) > 100)) {
            return 'Max value is 100%';
        }
        return '';
    };
}

class NewContractStore {
    @observable dateFrom: Date | null;
    @observable partTimePercentage: number | null = null;
    @observable officePercentage: number | null = null;
    @observable type: ContractType = ContractType.None;
    @observable typeModel: ContractTypeModel = ContractTypeModel.None;
    @observable comment: string | null = null;
    @observable isCabin: boolean = false;

    @observable partTimeMonthContractDefinition?: number;
    @observable partTimeDayContractDefinition?: number;
    @observable partTimeDaySplitType?: ContractPartTimeSplitType;
    @observable partTimeVariableBlockLength?: number;
    @observable partTimeVariableBlockPerYear?: number;
    @observable partTimeVariableDayBlockLength?: number;
    @observable partTimeVariableDaySplitType?: ContractPartTimeSplitType;
    @observable partTimeFixBlockDays?: number;
    @observable partTimeFixBlockFrequency?: number;
    @observable partTimeFixBlockBeginDay?: number;
    @observable partTimeFixBlockBeginMonth?: number;
    @observable partTimeFixDaysStartWeekPerContract?: number;
    @observable partTimeFixDaysPerWeek?: number;
    @observable partTimeFixDayContractDefinition?: number;
    @observable partTimeFixDayFirstDay?: number;
    @observable partTimeFixDaySecondDay?: number;
    @observable partTimeFixDaySplitType?: ContractPartTimeSplitType;
    @observable partTimeFixDayReason?: string;
    @observable partTimeSpecialDaysPerYear?: number;
    @observable partTimeSpecialVacEntitlement?: number;
    @observable isExternalEmployee?: boolean;

    @observable validationMessage: string | null = '';

    @observable selectedContract: PersonContractViewModel;

    private readonly _personId: string;
    private readonly _personFunctions: PersonFunctionViewModel[];

    constructor(params: NewContractDialogProps) {
        this._personId = params.personId;
        this._personFunctions = params.personFunctions;
        this.selectedContract = params.selectedContract;
        this.type = this.selectedContract.type;
        this.isExternalEmployee = this.selectedContract.isExternalEmployee;
        this.partTimePercentage = this.selectedContract.partTimePercentage ? this.selectedContract.partTimePercentage * 100 : null;
        this.officePercentage = this.selectedContract.officePercentage ? this.selectedContract.officePercentage * 100 : null;
        this.partTimeFixDaysPerWeek = 1;
    }

    @action.bound
    onSelectFromDate = (date?: Date | null): void => {
        this.dateFrom = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
        if (this.dateFrom) {
            this.isCabin = isCabinAt(this._personFunctions, this.dateFrom);
        } else {
            this.isCabin = false;
        }
    };

    @action.bound
    onPartTimeChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.partTimePercentage = newValue ? parseFloat(newValue) : null;
    };

    @action.bound
    onOfficeChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.officePercentage = newValue ? parseFloat(newValue) : null;
    };

    @action.bound
    onChangeType = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this.type = option.key as ContractType;
            this._handleTypeChange();
        }
    };

    @action.bound
    onCheckboxChange(ev?: React.FormEvent<HTMLInputElement | HTMLElement>, checked?: boolean) {
        this.isExternalEmployee = !!checked;
    }

    @action.bound
    onChangeTypeModel(newValue: ContractTypeModel) {
        this.typeModel = newValue;
    }

    @action.bound
    onChangePartTimeMonthContractDefinition(newValue: number) {
        this.partTimeMonthContractDefinition = newValue;
    }

    @action.bound
    onChangePartTimeDayContractDefinition(newValue: number) {
        this.partTimeDayContractDefinition = newValue;
    }

    @action.bound
    onChangePartTimeFixDayContractDefinition(newValue: number) {
        this.partTimeFixDayContractDefinition = newValue;
    }

    @action.bound
    onChangePartTimeDaySplitType(newValue: ContractPartTimeSplitType) {
        this.partTimeDaySplitType = newValue;
    }

    @action.bound
    onChangePartTimeVariableBlockLength(newValue: number) {
        this.partTimeVariableBlockLength = newValue;
    }

    @action.bound
    onChangePartTimeVariableBlockPerYear(newValue?: number) {
        this.partTimeVariableBlockPerYear = newValue;
    }

    @action.bound
    onChangePartTimeVariableDayBlockLength(newValue: number) {
        this.partTimeVariableDayBlockLength = newValue;
    }

    @action.bound
    onChangePartTimeVariableDaySplitType(newValue: ContractPartTimeSplitType) {
        this.partTimeVariableDaySplitType = newValue;
    }

    @action.bound
    onChangePartTimeFixDaySplitType(newValue?: ContractPartTimeSplitType) {
        this.partTimeFixDaySplitType = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockDays(newValue: number) {
        this.partTimeFixBlockDays = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockFrequency(newValue: number) {
        this.partTimeFixBlockFrequency = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockBeginDay(newValue: number) {
        this.partTimeFixBlockBeginDay = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockBeginMonth(newValue: number) {
        this.partTimeFixBlockBeginMonth = newValue;
    }

    @action.bound
    onChangePartTimeFixDaysStartWeekPerContract(newValue: number) {
        this.partTimeFixDaysStartWeekPerContract = newValue;
    }

    @action.bound
    onChangePartTimeFixDaysPerWeek(newValue: number) {
        this.partTimeFixDaysPerWeek = newValue;
        if (newValue === 1) {
            this.partTimeFixDaySecondDay = undefined;
        }
    }

    @action.bound
    onChangePartTimeFixDayFirstDay(newValue: number) {
        this.partTimeFixDayFirstDay = newValue;
    }

    @action.bound
    onChangePartTimeFixDaySecondDay(newValue: number) {
        this.partTimeFixDaySecondDay = newValue;
    }

    @action.bound
    onChangePartTimeFixDayReason(newValue?: string) {
        this.partTimeFixDayReason = newValue;
    }

    @action.bound
    onChangeComment = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.comment = newValue || null;
    };

    @action.bound
    onChangePartTimeSpecialDaysCount(newValue?: number) {
        this.partTimeSpecialDaysPerYear = newValue;
    }

    @action.bound
    onChangePartTimeSpecialVacEntitlement(newValue?: number) {
        this.partTimeSpecialVacEntitlement = newValue;
    }

    get contractTypeOptions() {
        const keys = Object.keys(ContractType).filter((key) => key !== ContractType.None);
        return keys.map(key => {
            return { key: key, text: key };
        });
    }

    @computed
    get flyingDutyValue() {
        let result;
        if (this.partTimePercentage) {
            result = this.partTimePercentage;
        }
        if (this.officePercentage && result) {
            result -= this.officePercentage;
        }
        return result ? result.toString() : '';
    }

    @computed
    get isValidForm() {
        return this.dateFrom && (this.officePercentage === null || this.officePercentage <= 100.000001) &&
            (this.officePercentage === null || this.partTimePercentage === null || this.officePercentage != null && this.partTimePercentage != null && this.officePercentage <= this.partTimePercentage)
            && ((this.type === ContractType.Fix && this.partTimePercentage !== null && this.partTimePercentage <= 100.000001) ||
                (this.type !== ContractType.Fix && (!this.partTimePercentage || this.partTimePercentage <= 100.000001))) &&
            (this.type !== ContractType.Tzv || (this.type === ContractType.Tzv && this.isValidPartTimeTzvForm));
    }

    @computed
    get isValidPartTimeTzvForm() {
        return ((this.typeModel === ContractTypeModel.TZV_Month && this.partTimeMonthContractDefinition) ||
            (this.typeModel === ContractTypeModel.TZV_Day && this.partTimeDayContractDefinition && this.partTimeDaySplitType) ||
            (this.typeModel === ContractTypeModel.TZV_VariableBlock && this.partTimeVariableBlockLength && this.partTimeVariableBlockPerYear) ||
            ((this.typeModel === ContractTypeModel.TZV_VariableDay || this.typeModel === ContractTypeModel.TZV_VariableDay_LhSff) && this.partTimeVariableDayBlockLength && this.partTimeVariableDaySplitType && this.partTimeVariableDaySplitType) ||
            (this.typeModel === ContractTypeModel.TZV_FixBlock && this.partTimeFixBlockDays && this.partTimeFixBlockFrequency && this.partTimeFixBlockBeginDay && this.partTimeFixBlockBeginMonth && this.isValidOffLengthFixBlock) ||
            ((this.typeModel === ContractTypeModel.TZV_FixDay || this.typeModel === ContractTypeModel.TZV_FixDay_Sh) && this.partTimeFixDaysPerWeek && this.partTimeFixDayFirstDay && (!this.partTimeFixDaySecondDay || this.partTimeFixDaySecondDay !== this.partTimeFixDayFirstDay) && (
                !this.isCabin || (this.partTimeFixDayContractDefinition)
            )) ||
            (this.typeModel === ContractTypeModel.TZV_FixDay_Mff && this.partTimeFixDaysStartWeekPerContract && this.partTimeFixDaysPerWeek && this.partTimeFixDayFirstDay && (!this.partTimeFixDaySecondDay || this.partTimeFixDaySecondDay !== this.partTimeFixDayFirstDay) && !this.isCabin) ||
            (this.typeModel === ContractTypeModel.TZV_Spez && this.partTimeSpecialDaysPerYear && this.partTimeSpecialVacEntitlement && this.partTimeSpecialDaysPerYear >= 1 && this.partTimeSpecialDaysPerYear <= 365 && this.partTimeSpecialVacEntitlement >= 0 && this.partTimeSpecialVacEntitlement <= 1) ||
            this.typeModel === ContractTypeModel.TZV_Ground ||
            this.typeModel === ContractTypeModel.TZV_R
        );
    }

    @computed
    get isValidOffLengthFixBlock() {
        return this.partTimeFixBlockDays && this.partTimeFixBlockFrequency && ((this.partTimeFixBlockDays * this.partTimeFixBlockFrequency) >= PartTimeTzv.minOffLength) && ((this.partTimeFixBlockDays * this.partTimeFixBlockFrequency) <= PartTimeTzv.maxOffLength);
    }

    @computed
    get offLengthFixBlockErrorMessage() {
        return this.partTimeFixBlockDays && this.partTimeFixBlockFrequency && `The calculated OFF-Length ( BlockLength * BlockFrequencyPerYear = ${this.partTimeFixBlockDays} * ${this.partTimeFixBlockFrequency} = ${this.partTimeFixBlockDays * this.partTimeFixBlockFrequency}) is not withing min/max range of ${PartTimeTzv.minOffLength}/${PartTimeTzv.maxOffLength}`;
    }

    @computed
    get needToShowEmploymentWarning(): boolean {
        return this.type === ContractType.Tzv && !!this.partTimePercentage && this.partTimePercentage > 99.9;
    }

    @computed
    get canChangeTypeModelPart(): boolean {
        if (this.type !== ContractType.Tzv)
            return true;

        return securityService.hasPermission(Permissions.ViewAddObjectPersonContractMainPart, !AzureAuth.isReadOnly) || securityService.hasPermission(Permissions.EditObjectPersonContractTZVPart, !AzureAuth.isReadOnly);
    }

    @action.bound
    async onSave(ignoreWarnings?: boolean): Promise<boolean> {
        let partTimeDayContractDefinition;
        if (this.typeModel === ContractTypeModel.TZV_Day) {
            partTimeDayContractDefinition = this.partTimeDayContractDefinition;
        } else if (this.typeModel === ContractTypeModel.TZV_FixDay && this.isCabin) {
            partTimeDayContractDefinition = this.partTimeFixDayContractDefinition;
        } else {
            partTimeDayContractDefinition = null;
        }
        let partTimeDaySplitType;
        if (this.typeModel === ContractTypeModel.TZV_Day) {
            partTimeDaySplitType = this.partTimeDaySplitType;
        } else if (this.typeModel === ContractTypeModel.TZV_FixDay && this.isCabin) {
            partTimeDaySplitType = this.partTimeFixDaySplitType;
        } else {
            partTimeDaySplitType = null;
        }
        const params = {
            externalId: this.selectedContract.externalId,
            personId: this._personId,
            dateFrom: this.dateFrom,
            partTimePercentage: this.partTimePercentage ? (this.partTimePercentage / 100) : null,
            officePercentage: this.officePercentage ? (this.officePercentage / 100) : null,
            type: this.type,
            typeModel: this.typeModel,
            isExternalEmployee: this.isExternalEmployee,
            comment: this.comment,

            partTimeMonthContractDefinition: this.typeModel === ContractTypeModel.TZV_Month ? this.partTimeMonthContractDefinition : null,
            partTimeDayContractDefinition,
            partTimeDaySplitType,
            partTimeVariableBlockLength: this.typeModel === ContractTypeModel.TZV_VariableBlock ? this.partTimeVariableBlockLength : null,
            partTimeVariableBlockPerYear: this.typeModel === ContractTypeModel.TZV_VariableBlock ? this.partTimeVariableBlockPerYear : null,
            partTimeVariableDayBlockLength: this.typeModel === ContractTypeModel.TZV_VariableDay || this.typeModel === ContractTypeModel.TZV_VariableDay_LhSff ? this.partTimeVariableDayBlockLength : null,
            partTimeVariableDaySplitType: this.typeModel === ContractTypeModel.TZV_VariableDay || this.typeModel === ContractTypeModel.TZV_VariableDay_LhSff ? this.partTimeVariableDaySplitType : null,
            partTimeFixBlockDays: this.typeModel === ContractTypeModel.TZV_FixBlock ? this.partTimeFixBlockDays : null,
            partTimeFixBlockFrequency: this.typeModel === ContractTypeModel.TZV_FixBlock ? this.partTimeFixBlockFrequency : null,
            partTimeFixBlockBeginDay: this.typeModel === ContractTypeModel.TZV_FixBlock ? this.partTimeFixBlockBeginDay : null,
            partTimeFixBlockBeginMonth: this.typeModel === ContractTypeModel.TZV_FixBlock ? this.partTimeFixBlockBeginMonth : null,
            partTimeFixDaysPerWeek: this.typeModel === ContractTypeModel.TZV_FixDay || this.typeModel === ContractTypeModel.TZV_FixDay_Sh || this.typeModel === ContractTypeModel.TZV_FixDay_Mff ? this.partTimeFixDaysPerWeek : null,
            partTimeFixDayFirstDay: this.typeModel === ContractTypeModel.TZV_FixDay || this.typeModel === ContractTypeModel.TZV_FixDay_Sh || this.typeModel === ContractTypeModel.TZV_FixDay_Mff ? this.partTimeFixDayFirstDay : null,
            partTimeFixDaySecondDay: this.typeModel === ContractTypeModel.TZV_FixDay || this.typeModel === ContractTypeModel.TZV_FixDay_Sh || this.typeModel === ContractTypeModel.TZV_FixDay_Mff ? this.partTimeFixDaySecondDay : null,
            partTimeFixDayReason: this.typeModel === ContractTypeModel.TZV_FixDay || this.typeModel === ContractTypeModel.TZV_FixDay_Sh || this.typeModel === ContractTypeModel.TZV_FixDay_Mff ? this.partTimeFixDayReason : null,
            partTimeSpecialDaysPerYear: this.typeModel === ContractTypeModel.TZV_Spez ? this.partTimeSpecialDaysPerYear : null,
            partTimeSpecialVacEntitlement: this.typeModel === ContractTypeModel.TZV_Spez ? this.partTimeSpecialVacEntitlement : null,
            partTimeFixDaysStartWeekPerContract: this.typeModel === ContractTypeModel.TZV_FixDay_Mff ? this.partTimeFixDaysStartWeekPerContract : null,
            ignoreWarnings: ignoreWarnings
        };

        const { data } = await ApiService.postData(ApiUrls.PersonContractUrl, params);
        if (data.hasErrors) {
            this.validationMessage = data.errors.join(' ');
            return false;
        }

        if (data.hasWarnings && !ignoreWarnings) {
            if (await modalService.showConfirmation(data.warnings.join('\r\n '), {
                color: 'warning',
                confirmButtonTitle: 'Ignore and save anyway'
            })) {
                return await this.onSave(true);
            }
            return false;
        }

        this.validationMessage = null;
        NotificationHandler.showSuccess('New Person Contract Model was added', 'Success message');
        return true;
    }

    private _handleTypeChange() {
        if ((this.type === ContractType.Fix || this.type === ContractType.Fse || this.type === ContractType.None)
            && this.typeModel !== ContractTypeModel.None) {
            this.typeModel = ContractTypeModel.None;
        } else if (this.type === ContractType.Tzv) {
            this.typeModel = ContractTypeModel.TZV_Ground;
        } else if (this.type === ContractType.Freelance && this.typeModel !== ContractTypeModel.None && this.typeModel !== ContractTypeModel.FL_S
            && this.typeModel !== ContractTypeModel.FL_A && this.typeModel !== ContractTypeModel.FL_C && this.typeModel !== ContractTypeModel.FL_B) {
            this.typeModel = ContractTypeModel.None;
        }
        if (this.type === ContractType.Fix) {
            this.partTimePercentage = 100;
        }
    }
}